/**
 * Per-section style override → inline CSS custom properties.
 *
 * Phase C (2026-05-11). Every section's data carries an optional `style`
 * block; this helper maps that block onto scoped CSS variables emitted
 * on the section root. Components reference `var(--section-*)` with
 * theme-level fallback, so unset fields fall through to the global
 * theme tokens emitted on the body element by theme-tokens.ts. The
 * body-level invariant ("one-shot, no per-section overrides") is
 * preserved — we layer scoped vars on top, never mutate the body.
 *
 * Values here are SEMANTIC (e.g. `surface_tone: 'inverse'`), not hex.
 * Per-instance hex is intentionally out of scope.
 */
import type { CSSProperties } from 'react'
import { resolveSectionSurface, type SectionSurfaceBrand } from '@sifly/shared-utils'

export type SectionStyleOverrides = {
  surface_tone?: 'canvas' | 'card' | 'elevated' | 'inverse' | 'accent'
  /** Newer background model (supersedes surface_tone). page|card|elevated|primary|secondary|tertiary|dark. */
  surface_source?: 'page' | 'card' | 'elevated' | 'primary' | 'secondary' | 'tertiary' | 'dark'
  /** −60…60. >0 darker, <0 lighter; only meaningful for primary/secondary/tertiary sources. */
  surface_shade?: number
  density?: 'compact' | 'comfortable' | 'spacious'
  accent?: 'brand' | 'secondary' | 'muted' | 'highlight'
  radius?: 'sharp' | 'rounded' | 'pill'
  divider_top?: boolean
  divider_bottom?: boolean
  headline_weight?: 'normal' | 'bold' | 'display'
  image_treatment?: 'plain' | 'rounded' | 'tilted' | 'frame'
  composition?: 'centered' | 'split' | 'asymmetric'
  /** Container width knob → --section-max-width (envelope extensions, 2026-07-05). */
  max_width?: 'prose' | 'content' | 'wide' | 'full'
  /** Horizontal gutter knob → --section-padding-x. */
  padding_x?: 'none' | 'compact' | 'default' | 'spacious'
  /** Wrapper text-align fallback → --section-text-align + inherited text-align.
   *  Kind-specific alignment props / classes win over this envelope value. */
  text_align?: 'start' | 'center' | 'end'
}

// The two surface tones the dispatcher paints as a DARK band (white text on
// a dark/brand background). Renderers that draw their own content on top of
// the band use this to flip foreground colors to light — otherwise dark
// text renders dark-on-dark. Single source of truth so every grid/band kind
// agrees on which tones are dark.
export const DARK_SURFACE_TONES: ReadonlySet<NonNullable<SectionStyleOverrides['surface_tone']>> = new Set([
  'inverse',
  'accent',
])

/** True when the section's surface_tone paints a dark band. */
export function isDarkSurfaceTone(style?: Pick<SectionStyleOverrides, 'surface_tone'> | null): boolean {
  return !!style?.surface_tone && DARK_SURFACE_TONES.has(style.surface_tone)
}

/**
 * Heading text-color class for a section whose band may read dark.
 *
 * On a dark band (`onDark`) the heading returns an EMPTY class so it inherits
 * the wrapper's `color: var(--section-on-surface)` — the guaranteed-light
 * foreground `resolveSectionStyle` paints for that band. On a light band it
 * returns the default dark heading token. A hardcoded `text-[var(--color-text-primary)]`
 * would override the inherited on-surface color and render dark-on-dark
 * (~2:1) on a dark-brand tenant. This is the single source of truth for the
 * `onDark ? '' : dark-token` idiom the cta_band CenteredSlide/BandSplit
 * headings inline; booking_cta consumes it so both stay legible on the same brand.
 */
export function onDarkHeadingClass(onDark?: boolean): string {
  return onDark ? '' : 'text-[var(--color-text-primary)]'
}

const HEADLINE_WEIGHT_VALUE: Record<NonNullable<SectionStyleOverrides['headline_weight']>, string> = {
  normal: '600',
  bold: '800',
  display: '900',
}

const HEADLINE_WEIGHT_FAMILY: Record<NonNullable<SectionStyleOverrides['headline_weight']>, string> = {
  normal: 'var(--font-heading, var(--font-sans, ui-sans-serif))',
  bold: 'var(--font-heading, var(--font-sans, ui-sans-serif))',
  display: 'var(--font-display, var(--font-heading, var(--font-sans, ui-sans-serif)))',
}

const IMAGE_TREATMENT_RADIUS: Record<NonNullable<SectionStyleOverrides['image_treatment']>, string> = {
  plain: '0',
  rounded: 'var(--radius-lg, 0.75rem)',
  tilted: 'var(--radius-lg, 0.75rem)',
  frame: '0',
}

const SURFACE_TONE_BG: Record<NonNullable<SectionStyleOverrides['surface_tone']>, string> = {
  canvas: 'var(--color-surface-canvas)',
  card: 'var(--color-surface-card)',
  // Alias of `canvas` — see sectionSurface.ts's NEUTRAL.elevated for why (the
  // raised-UI token equals the canvas by design, so this preset has always
  // rendered identically to `canvas`). Kept in lockstep with that file.
  elevated: 'var(--color-surface-canvas)',
  inverse: 'var(--color-surface-inverse)',
  accent: 'var(--accent-surface, var(--brand-primary, var(--color-cta, #2563eb)))',
}

const SURFACE_TONE_ON: Record<NonNullable<SectionStyleOverrides['surface_tone']>, string> = {
  canvas: 'var(--color-text-primary)',
  card: 'var(--color-on-surface-card)',
  // Alias of `canvas` (see SURFACE_TONE_BG above). `--color-on-surface-card`
  // resolves to `--color-text-primary`, so this is the same value it always was.
  elevated: 'var(--color-text-primary)',
  inverse: 'var(--color-on-surface-inverse)',
  // The accent band is now guaranteed-dark (see --accent-surface), so its
  // foreground must be guaranteed-LIGHT — NOT autoContrast(accent), which goes
  // dark for a pale accent color and would render dark-on-dark.
  accent: 'var(--color-on-surface-inverse, #f8fafc)',
}

const DENSITY_PADDING: Record<NonNullable<SectionStyleOverrides['density']>, string> = {
  compact: 'var(--space-section-tight)',
  comfortable: 'var(--space-section-comfortable)',
  spacious: 'var(--space-section-spacious)',
}

const ACCENT_COLOR: Record<NonNullable<SectionStyleOverrides['accent']>, string> = {
  brand: 'var(--brand-primary, var(--color-cta, #2563eb))',
  secondary: 'var(--brand-secondary, var(--color-text-secondary, #6b7280))',
  muted: 'var(--color-text-muted, var(--color-text-secondary, #6b7280))',
  // The tenant's second accent; unset it falls to the CTA accent, so this
  // knob is always safe to choose.
  highlight: 'var(--color-highlight, var(--brand-accent, var(--color-cta, #2563eb)))',
}

const RADIUS_VALUE: Record<NonNullable<SectionStyleOverrides['radius']>, string> = {
  sharp: 'var(--radius-sm)',
  rounded: 'var(--radius-lg)',
  pill: 'var(--radius-2xl, 1.5rem)',
}

// Container knobs (envelope extensions, 2026-07-05). Section container
// classes were swept to `max-w-[var(--section-max-width,<literal>)]` /
// `px-[var(--section-padding-x,<literal>)]`, so unset knobs fall through to
// each kind's existing literal. `content` re-states the page width var so an
// explicit choice still overrides a kind whose container is narrower than
// the page (e.g. form_embed's 42rem).
const MAX_WIDTH_VALUE: Record<NonNullable<SectionStyleOverrides['max_width']>, string> = {
  prose: 'var(--max-width-prose, 720px)',
  content: 'var(--max-width-page, 1280px)',
  wide: '1440px',
  full: 'none',
}

const PADDING_X_VALUE: Record<NonNullable<SectionStyleOverrides['padding_x']>, string> = {
  none: '0px',
  compact: '0.75rem',
  default: '1rem',
  spacious: '2.5rem',
}

// On-dark CTA treatment. The global CTA tokens (set once on <body> in
// layout.tsx) fill the primary with the brand ACCENT and outline the secondary
// in brand PRIMARY — both contrast-checked against the LIGHT page background.
// On a dark band (`accent` paints brand-primary, `inverse` paints the dark
// surface) the accent fill collapses into the band (accent falls back to
// primary for most tenants) and the brand-primary outline goes dark-on-dark,
// so the button SHAPE disappears even though the label stays legible.
//
// Re-scoping the same `--cta-*` custom properties on the dark-band wrapper
// (an ancestor of every CTA inside the band) inverts every `.cta-primary` /
// `.cta-secondary` via CSS-var cascade — no per-callsite edits, and it covers
// any band section with CTAs automatically. `1.5px` borders match
// BORDER_RESERVE_WIDTH so the buttons keep the same footprint as elsewhere.
//
//   Primary  → solid white fill + brand-color label. White is the only
//              guaranteed-light fill: the band's own foreground token
//              (--section-on-surface) is NOT reliable here — for `accent`
//              bands it resolves to autoContrast(accent), which is DARK when
//              the tenant's accent is light, so reusing it would leave a dark
//              button on a dark band. The label reads `--cta-on-dark-primary-fg`
//              (layout.tsx): the brand primary when it clears AA contrast on
//              white, else a dark neutral — so a pale brand primary can't turn
//              the label near-white-on-white. Falls back to brand primary if
//              the token is unset.
//   Secondary → transparent + BRAND-ACCENT outline, white label. Was a white
//              outline, matching what heroes/CTA bands hand-rolled over photo
//              backgrounds; both moved to the accent together (2026-07-30) so
//              a hero and a dark band on the same page can't disagree. White
//              was the safe generic, but it rendered every tenant's secondary
//              identically and read as a different brand than the accent-filled
//              primary beside it. `--cta-on-dark-accent` (layout.tsx) is the
//              accent pre-floored to 4.5:1 on black, which is what retires the
//              legibility argument for white; the `#fff` fallback preserves the
//              old behavior anywhere the token isn't set. The LABEL stays white
//              — see ON_DARK_OUTLINE in lib/ctaTone.ts for why.
//
// Tertiary (muted) is deliberately NOT inverted here. Its only band-section
// consumer is feature_card_grid's `cta_display: "button"`, where the button
// sits on the CARD, not the band — and card darkness ≠ band darkness (the
// `accent` tone floats WHITE cards on a dark band). A band-level inversion
// would frost the button inside those white cards. The component branches on
// its own card chrome (`chrome.dark`) instead.
const DARK_BAND_CTA_VARS: Record<string, string> = {
  '--cta-primary-bg': '#ffffff',
  '--cta-primary-fg': 'var(--cta-on-dark-primary-fg, var(--brand-primary, var(--color-primary, #0f172a)))',
  '--cta-primary-border': '1.5px solid transparent',
  '--cta-secondary-bg': 'transparent',
  '--cta-secondary-fg': '#ffffff',
  '--cta-secondary-border': '1.5px solid var(--cta-on-dark-accent, rgba(255, 255, 255, 0.7))',
}

/**
 * Returns inline-style CSS custom properties for a section root.
 * Unset fields are absent from the returned object — components fall
 * back to global theme tokens via their own `var(..., fallback)` calls.
 *
 * Components should also pass the same style to `applySectionSurface`
 * if they want background + foreground colors applied on the root.
 */
// Scoped CSS vars that DON'T depend on the surface (density / accent / radius /
// headline / image). Surface vars are layered on by resolveSectionStyle so the
// new (source+shade) and legacy (tone) paths share one composition point.
function applyScopedVars(style: SectionStyleOverrides): Record<string, string> {
  const out: Record<string, string> = {}
  if (style.density) out['--section-padding-y'] = DENSITY_PADDING[style.density]
  if (style.accent) out['--section-accent'] = ACCENT_COLOR[style.accent]
  if (style.radius) out['--section-radius'] = RADIUS_VALUE[style.radius]
  if (style.headline_weight) {
    out['--section-headline-weight'] = HEADLINE_WEIGHT_VALUE[style.headline_weight]
    out['--section-headline-family'] = HEADLINE_WEIGHT_FAMILY[style.headline_weight]
  }
  if (style.image_treatment) out['--section-image-radius'] = IMAGE_TREATMENT_RADIUS[style.image_treatment]
  if (style.max_width) out['--section-max-width'] = MAX_WIDTH_VALUE[style.max_width]
  if (style.padding_x) out['--section-padding-x'] = PADDING_X_VALUE[style.padding_x]
  // The var serves swept container classes with a hardcoded fallback
  // (`[text-align:var(--section-text-align,center)]`); the inherited
  // text-align (set on the wrapper in resolveSectionStyle) covers kinds with
  // no wrapper-level alignment class. Kind-specific alignment classes deeper
  // in the tree beat the inherited value automatically.
  if (style.text_align) out['--section-text-align'] = style.text_align
  return out
}

/**
 * The single place a section's surface + darkness are decided. Returns the inline
 * style props for the section wrapper AND the authoritative `onDark` flag every
 * band/grid renderer reads — so the band background and its text/CTA contrast can
 * never disagree (the dark-on-dark class of bug). Two paths:
 *   - NEW: `surface_source` (+ `surface_shade`) → `resolveSectionSurface`; darkness
 *     is computed from the RESOLVED color (dynamic for brand sources). Brand hexes
 *     are threaded from the page via `resolved.brandSurface` — needed only for
 *     primary/secondary sources; neutral sources resolve without it.
 *   - LEGACY: `surface_tone` → the fixed 5-preset lookup, byte-identical to before.
 *
 * Issue 3 (a class-based .cta-* inside an OPAQUE-LIGHT panel within a dark band would
 * inherit the inverted white --cta-* fill and vanish): audited 2026-06-20 — no such
 * site exists; if one is added, reset --section-on-surface + the --cta-* family from
 * the immutable --page-cta-* mirror tokens on that panel.
 */
export function resolveSectionStyle(
  style?: SectionStyleOverrides,
  brand?: SectionSurfaceBrand,
): { styleProps: CSSProperties; onDark: boolean } {
  if (!style) return { styleProps: {}, onDark: false }
  const out: Record<string, string> = applyScopedVars(style)
  let bg: string | null = null
  let on: string | null = null
  let onMuted: string | null = null
  let onDark = false

  const fromSource = style.surface_source ? resolveSectionSurface(style, brand ?? {}) : null
  if (fromSource) {
    bg = fromSource.cssBg
    on = fromSource.onColor
    onMuted = fromSource.onColorMuted
    onDark = fromSource.isDark
  } else if (style.surface_tone) {
    bg = SURFACE_TONE_BG[style.surface_tone]
    on = SURFACE_TONE_ON[style.surface_tone]
    onDark = isDarkSurfaceTone(style)
    // A dark band needs a muted foreground too. Without this the legacy path
    // emitted none, so every `var(--section-on-surface-muted, …)` caller fell
    // back to the page secondary — dark grey on dark. Matches what the newer
    // surface_source path resolves for its `dark` preset.
    if (onDark) onMuted = 'var(--color-on-surface-inverse-muted)'
  }
  if (bg) out['--section-surface'] = bg
  if (on) out['--section-on-surface'] = on
  // Band-aware muted foreground (subtitles/captions). Emitted for brand sources
  // with a resolved color, and for the legacy dark tones (`inverse` / `accent`)
  // — see the branch above; without it those bands rendered dark grey on dark.
  // Muted callers read `var(--section-on-surface-muted, var(--color-text-secondary))`,
  // so neutral and legacy LIGHT tones still fall through to the page secondary.
  if (onMuted) out['--section-on-surface-muted'] = onMuted

  const composed: CSSProperties = { ...(out as CSSProperties) }
  if (bg) composed.background = 'var(--section-surface)'
  if (on) composed.color = 'var(--section-on-surface)'
  if (onDark) Object.assign(composed, DARK_BAND_CTA_VARS)
  if (style.density) {
    composed.paddingTop = 'var(--section-padding-y)'
    composed.paddingBottom = 'var(--section-padding-y)'
  }
  if (style.divider_top) composed.borderTop = '1px solid var(--color-border)'
  if (style.divider_bottom) composed.borderBottom = '1px solid var(--color-border)'
  if (style.text_align) composed.textAlign = style.text_align
  return { styleProps: composed, onDark }
}

/**
 * Back-compat shim: inline style props only. Legacy callers and the no-brand path
 * get output identical to the pre-feature helper; the dispatcher uses
 * `resolveSectionStyle` directly so it can also read `onDark`.
 */
export function applySectionSurface(style?: SectionStyleOverrides): CSSProperties {
  return resolveSectionStyle(style).styleProps
}

/**
 * Full-bleed band-padding guard (2026-07-22; generalized 2026-08-03).
 *
 * A full-bleed variant's photo/video fills the SECTION (`absolute inset-0` /
 * `fill`), not the dispatcher wrapper around it — so `density` padding on
 * the wrapper renders as solid band-color letterbox bars above and below the
 * media (128px each at `spacious`). The style block is usually
 * agent-authored (WordSmith/SiteSmith pick these knobs), which made this an
 * easy foot-gun: Adventure Elevated's home hero shipped with
 * `density: 'spacious'` + `surface_source: 'dark'` and got teal bars that
 * also defeated the brand_showcase header's transparent-overlay mode; the
 * same tenant's /location page then shipped a `band_full_bleed` cta_band
 * with `density: 'spacious'` + `surface_source: 'page'` and got white bars.
 *
 * These variants' contract is "media edge-to-edge"; outer band padding
 * contradicts it by definition (slides own their spacing via `min_height` +
 * internal padding), so the guard drops `density` unconditionally for the
 * variant rather than trying to detect resolved media. Everything else in
 * the style block (surface, text_align, …) passes through — the surface
 * still drives the on-dark CTA treatment even when invisible behind media.
 * Other variants and section kinds are untouched.
 */
const FULL_BLEED_VARIANT_BY_KIND: Record<string, string> = {
  hero: 'full_bleed_media',
  cta_band: 'band_full_bleed',
  image_break: 'full_bleed',
}

export function stripFullBleedBandPadding(
  kind: string,
  variant: unknown,
  style?: SectionStyleOverrides,
): SectionStyleOverrides | undefined {
  if (!style?.density || FULL_BLEED_VARIANT_BY_KIND[kind] !== variant) return style
  const rest = { ...style }
  delete rest.density
  return rest
}
